import React, { useState } from 'react';
import { View, Text, StyleSheet, TextInput } from 'react-native';
import { VisualData, resolveTokens } from '@eduwrite/shared';

interface Props {
    data: Extract<VisualData, { type: 'column_layout' }>;
    context: Record<string, number | string>;
    onInput: (input: string) => void;
}

export const ColumnLayoutVisual: React.FC<Props> = ({ data, context, onInput }) => {
    const columns = data.columns;
    const operands = data.operands.map((op) => resolveTokens(String(op), context));
    const operator = data.operator;
    const blanks = data.blanks;

    const [inputs, setInputs] = useState<Record<string, string>>({});

    const handleChange = (key: string, value: string) => {
        const updated = { ...inputs, [key]: value };
        setInputs(updated);
        onInput(JSON.stringify(updated));
    };

    const renderCell = (content: string, rowIdx: number, colIdx: number, isBlank: boolean, isCarryRow: boolean) => {
        const key = `${rowIdx}-${colIdx}`;
        if (isBlank) {
            return (
                <TextInput
                    key={key}
                    style={[
                        styles.blankInput,
                        isCarryRow && styles.carryBlank,
                    ]}
                    value={inputs[key] || ''}
                    onChangeText={(text) => handleChange(key, text)}
                    keyboardType="numeric"
                    maxLength={3}
                    accessibilityLabel={`Blank at row ${rowIdx + 1}, column ${colIdx + 1}`}
                />
            );
        }
        return (
            <View key={key} style={[styles.cell, isCarryRow && styles.carryCell]}>
                <Text style={styles.cellText} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{content}</Text>
            </View>
        );
    };

    // Build rows: optional carry row + operand rows + result row
    const rows: { cells: (string | null)[]; isCarryRow: boolean; isResultRow: boolean }[] = [];

    // Carry row (if blanks includes 'carry')
    if (blanks.includes('carry')) {
        rows.push({
            cells: columns.map(() => null),
            isCarryRow: true,
            isResultRow: false,
        });
    }

    operands.forEach((op) => {
        const digits = op.split('').reverse();
        const padded = columns.map((_c, i) => {
            const digit = digits[i];
            return digit || '';
        }).reverse();
        rows.push({ cells: padded, isCarryRow: false, isResultRow: false });
    });

    // Result row
    rows.push({
        cells: columns.map((_c, _i) => (blanks.includes('result') ? null : '')),
        isCarryRow: false,
        isResultRow: true,
    });

    // Auto-check result row: if all result blanks have input, compute and highlight
    const resultRowIdx = rows.length - 1;
    const resultInputs = columns.map((_c, cIdx) => inputs[`${resultRowIdx}-${cIdx}`] || '');
    const allResultFilled = blanks.includes('result') && resultInputs.every((v) => v.length > 0);

    // Simple auto-check: compute expected result from operands
    const expectedResult = operator === '+'
        ? operands.reduce((sum, op) => sum + Number(op), 0)
        : operator === '-'
            ? Number(operands[0]) - Number(operands[1] || 0)
            : null;
    const expectedDigits = expectedResult !== null ? String(expectedResult).split('').reverse() : [];
    const isResultCorrect = allResultFilled && expectedDigits.length > 0 && columns.every((_c, cIdx) => {
        const fromRight = columns.length - 1 - cIdx;
        const expected = expectedDigits[fromRight] || '';
        return inputs[`${resultRowIdx}-${cIdx}`] === expected;
    });

    return (
        <View style={styles.container}>
            <Text style={styles.operator} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{operator}</Text>
            <View style={[styles.grid, isResultCorrect && styles.gridCorrect]}>
                {/* Header */}
                <View style={styles.row}>
                    {columns.map((col, idx) => (
                        <Text key={idx} style={styles.headerCell} allowFontScaling={true} maxFontSizeMultiplier={1.5}>{col}</Text>
                    ))}
                </View>
                {/* Rows */}
                {rows.map((row, rIdx) => (
                    <View key={rIdx} style={styles.row}>
                        {row.cells.map((cell, cIdx) => {
                            const isBlank = cell === null || (row.isResultRow && blanks.includes('result'));
                            return renderCell(cell ?? '', rIdx, cIdx, isBlank, row.isCarryRow);
                        })}
                    </View>
                ))}
            </View>
            {isResultCorrect && (
                <Text style={styles.correctBadge} allowFontScaling={true} maxFontSizeMultiplier={1.5}>✓ Correct!</Text>
            )}
        </View>
    );
};

const styles = StyleSheet.create({
    container: {
        alignItems: 'center',
        padding: 12,
    },
    operator: {
        fontSize: 24,
        fontWeight: '800',
        color: '#1E293B',
        marginBottom: 8,
    },
    grid: {
        borderWidth: 2,
        borderColor: '#1E293B',
        borderRadius: 8,
        overflow: 'hidden',
    },
    row: {
        flexDirection: 'row',
        borderBottomWidth: 1,
        borderBottomColor: '#E2E8F0',
    },
    headerCell: {
        width: 64,
        paddingVertical: 8,
        textAlign: 'center',
        fontSize: 12,
        fontWeight: '700',
        color: '#64748B',
        backgroundColor: '#F1F5F9',
        borderRightWidth: 1,
        borderRightColor: '#E2E8F0',
    },
    cell: {
        width: 64,
        height: 48,
        alignItems: 'center',
        justifyContent: 'center',
        borderRightWidth: 1,
        borderRightColor: '#E2E8F0',
    },
    cellText: {
        fontSize: 20,
        fontWeight: '700',
        color: '#1E293B',
    },
    blankInput: {
        width: 64,
        height: 48,
        borderRightWidth: 1,
        borderRightColor: '#E2E8F0',
        textAlign: 'center',
        fontSize: 20,
        fontWeight: '700',
        color: '#3B82F6',
        backgroundColor: '#EFF6FF',
    },
    carryBlank: {
        backgroundColor: '#FEF9C3',
        color: '#854D0E',
    },
    carryCell: {
        backgroundColor: '#FEF9C3',
    },
    gridCorrect: {
        borderColor: '#22c55e',
        borderWidth: 3,
    },
    correctBadge: {
        marginTop: 8,
        fontSize: 16,
        fontWeight: '700',
        color: '#22c55e',
    },
});
